import React from "react";
import { useLocation, useParams } from "react-router-dom";
import Button from "@/components/ui/Button.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  resolveWorldlineSponsorLink,
  startWorldlineSponsorCheckout,
  verifyWorldlineSponsorCheckout,
} from "@/services/worldlineHostedCheckoutService.js";

function formatMoney(minor, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: String(currency || "ZAR") }).format(Number(minor || 0) / 100);
  } catch {
    return `${String(currency || "").trim()} ${(Number(minor || 0) / 100).toFixed(2)}`;
  }
}

function statusMessage(state, t) {
  const key = String(state || "pending").toLowerCase();
  const messages = {
    successful: t("worldline.sponsor.success", "Payment confirmed. Your healthcare appointment is funded."),
    paid: t("worldline.sponsor.success", "Payment confirmed. Your healthcare appointment is funded."),
    cancelled: t("worldline.sponsor.cancelled", "Payment was cancelled. Ask the patient for a new sponsor link if you still wish to pay."),
    declined: t("worldline.sponsor.declined", "Payment was declined. No healthcare appointment was created."),
    expired: t("worldline.sponsor.expired", "This sponsor payment link has expired."),
    failed: t("worldline.sponsor.failed", "Payment could not be completed. No healthcare appointment was created."),
  };
  return messages[key] || t("worldline.sponsor.pending", "This payment is still being processed. Please refresh shortly.");
}

export default function SponsorWorldlinePayment() {
  const { opaqueToken = "" } = useParams();
  const location = useLocation();
  const { t, languageCode, formatDate } = useLanguage();
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [starting, setStarting] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [error, setError] = React.useState("");
  const [paymentState, setPaymentState] = React.useState("");

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await resolveWorldlineSponsorLink(opaqueToken);
      setSummary(result);
      setPaymentState(String(result?.payment_state || ""));
    } catch (nextError) {
      setSummary(null);
      setError(nextError?.message || t("worldline.sponsor.unavailable", "This sponsor payment link is unavailable."));
    } finally {
      setLoading(false);
    }
  }, [opaqueToken, t]);

  React.useEffect(() => { void refresh(); }, [refresh]);
  React.useEffect(() => {
    if (!location.search) return;
    let active = true;
    async function verify() {
      setVerifying(true);
      try {
        const result = await verifyWorldlineSponsorCheckout(opaqueToken);
        if (active) setPaymentState(String(result?.state || "pending"));
      } catch (nextError) {
        if (active) setError(nextError?.message || t("worldline.sponsor.verificationUnavailable", "Payment verification is temporarily unavailable. Please retry."));
      } finally {
        if (active) setVerifying(false);
      }
    }
    void verify();
    return () => { active = false; };
  }, [location.search, opaqueToken, t]);

  async function start() {
    setStarting(true);
    setError("");
    try {
      const result = await startWorldlineSponsorCheckout({ token: opaqueToken, locale: languageCode });
      const redirectUrl = String(result?.redirect_url || "").trim();
      if (!redirectUrl.startsWith("https://")) throw new Error("Hosted checkout was not returned.");
      window.location.assign(redirectUrl);
    } catch (nextError) {
      setError(nextError?.message || t("worldline.sponsor.checkoutUnavailable", "Worldline sandbox checkout is unavailable."));
      setStarting(false);
    }
  }

  const state = paymentState || summary?.payment_state || "pending";
  const terminal = ["successful", "paid", "cancelled", "declined", "expired", "failed", "fulfilment_blocked"].includes(String(state).toLowerCase());
  return (
    <main className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))] px-4 py-8 text-on-surface sm:px-6">
      <section className="mx-auto w-full max-w-xl rounded-[20px] border border-[rgb(var(--afrimed-outline))] bg-[rgb(var(--afrimed-surface))] p-5 shadow-card sm:p-8">
        <div className="text-title-medium text-[rgb(var(--afrimed-primary))]">AfriMed™</div>
        <h1 className="mt-4 text-headline-small text-on-surface">{t("worldline.sponsor.title", "Secure sponsor payment")}</h1>
        <p className="mt-2 text-body-medium text-on-surface-var">{t("worldline.sponsor.privacy", "This link can fund one healthcare appointment. It does not provide access to a patient account or medical information.")}</p>

        {loading ? <p className="mt-6 text-body-medium text-on-surface-var">{t("worldline.sponsor.loading", "Loading secure payment summary…")}</p> : null}
        {error ? <div className="mt-6 rounded-xl bg-[var(--state-critical-08)] p-4 text-body-medium text-[rgb(var(--schemes-critical))]">{error}</div> : null}
        {summary ? <>
          <dl className="mt-6 grid gap-4 rounded-xl bg-[rgb(var(--clr-surface-container))] p-4 text-body-medium">
            <div><dt className="text-on-surface-var">{t("worldline.sponsor.clinician", "Clinician")}</dt><dd className="mt-1 font-medium text-on-surface">{summary.clinician_name}</dd></div>
            <div><dt className="text-on-surface-var">{t("worldline.sponsor.appointment", "Appointment")}</dt><dd className="mt-1 font-medium text-on-surface">{formatDate(summary.appointment_at, { dateStyle: "medium", timeStyle: "short" })}</dd></div>
            <div><dt className="text-on-surface-var">{t("worldline.sponsor.amount", "Amount")}</dt><dd className="mt-1 font-medium text-on-surface">{formatMoney(summary.amount_minor, summary.currency)}</dd></div>
          </dl>
          {verifying ? <p className="mt-5 text-body-medium text-on-surface-var">{t("worldline.sponsor.verifying", "Verifying payment…")}</p> : null}
          {terminal ? <div className="mt-5 rounded-xl bg-[var(--state-primary-08)] p-4 text-body-medium text-on-surface">{statusMessage(state, t)}</div> : null}
          {!terminal && !verifying ? <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Button size="lg" loading={starting} disabled={starting} onClick={start}>{t("worldline.sponsor.pay", "Pay securely with Worldline")}</Button>
            <Button size="lg" variant="tonal" disabled={starting} onClick={() => void refresh()}>{t("worldline.sponsor.refresh", "Refresh payment status")}</Button>
          </div> : null}
          <p className="mt-4 text-body-small text-on-surface-var">{t("worldline.sponsor.sandbox", "Worldline sandbox — no real payment will be made.")}</p>
        </> : null}
      </section>
    </main>
  );
}
